import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Download, Loader2, ShieldCheck, Banknote } from "lucide-react";
import { API } from "../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-staff permission toggles, rendered from the BACKEND registry
 * (`GET /auth/staff/permissions/registry`) rather than a hard-coded
 * list, so adding a third flag is a backend-only change.
 *
 * All flags are off by default. A flag with unmet dependencies is
 * disabled here AND rejected by the server — the UI is a courtesy, not
 * the enforcement.
 */
export const StaffPermissionToggles = ({ member, registry, onChanged }) => {
  const [saving, setSaving] = useState(null);
  const flags = member.permissions || {};

  const save = async (key, value) => {
    const next = { ...flags, [key]: value };
    // Turning off a flag others depend on turns those off too, so the
    // admin never saves a combination the server will reject.
    if (!value) {
      registry.forEach((p) => {
        if ((p.requires || []).includes(key)) next[p.key] = false;
      });
    }
    setSaving(key);
    try {
      const r = await axios.put(
        `${API}/auth/staff/${member.user_id}/permissions`,
        { permissions: next });
      onChanged(member.user_id, r.data.permissions);
      toast.success(r.data.changed ? "Permission updated" : "No change");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update permission");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-2" data-testid={`staff-permissions-${member.user_id}`}>
      {registry.map((p) => {
        const unmet = (p.requires || []).filter((r) => !flags[r]);
        const blocked = unmet.length > 0 && !flags[p.key];
        return (
          <div key={p.key} className="flex items-start gap-3">
            <Switch
              checked={!!flags[p.key]}
              disabled={blocked || saving === p.key}
              onCheckedChange={(v) => save(p.key, v)}
              data-testid={`perm-toggle-${p.key}-${member.user_id}`}
            />
            <div className="min-w-0">
              <p className="text-sm text-white">
                {p.label}
                {saving === p.key && (
                  <Loader2 className="w-3 h-3 ml-2 inline animate-spin text-slate-400" />
                )}
              </p>
              <p className="text-[11px] text-slate-500">{p.help}</p>
              {blocked && (
                <p className="text-[11px] text-amber-400">
                  Turn on &ldquo;{registry.find((x) => x.key === unmet[0])?.label}
                  &rdquo; first
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * "Cash taken by staff" — the till reconciliation list. Every cash
 * payment records who, when, how much and which booking, including
 * cash an admin took, so the total here is the whole till.
 */
export const CashTakenPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await axios.get(`${API}/auth/staff/cash-payments`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load cash payments");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const qs = new URLSearchParams({ format: "csv" });
    if (from) qs.set("date_from", from);
    if (to) qs.set("date_to", to);
    window.open(`${API}/auth/staff/cash-payments?${qs.toString()}`, "_blank");
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="cash-taken-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Banknote className="w-4 h-4 text-emerald-400" />
              Cash taken by staff
            </h3>
            <p className="text-[11px] text-slate-500">
              Reconcile the till. Includes cash taken by admins.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] uppercase text-slate-500 block">From</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                     className="bg-slate-900 border-slate-700 text-white h-8 w-36"
                     data-testid="cash-filter-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 block">To</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                     className="bg-slate-900 border-slate-700 text-white h-8 w-36"
                     data-testid="cash-filter-to" />
            </div>
            <Button size="sm" variant="outline" onClick={exportCsv}
                    className="border-slate-600 text-slate-300 h-8"
                    data-testid="cash-export-csv-btn">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}

        {!loading && data && (
          <>
            <div className="flex gap-4">
              <div>
                <p className="text-[10px] uppercase text-slate-500">Total cash</p>
                <p className="text-lg font-mono text-emerald-400"
                   data-testid="cash-total">{money(data.total)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-500">Payments</p>
                <p className="text-lg font-mono text-white">{data.payment_count}</p>
              </div>
            </div>

            {data.by_staff.length === 0 ? (
              <p className="text-sm text-slate-500">
                No cash payments recorded in this period.
              </p>
            ) : (
              <div className="space-y-3">
                {data.by_staff.map((s) => (
                  <div key={s.taken_by_id} className="rounded border border-slate-700 p-2"
                       data-testid={`cash-by-staff-${s.taken_by_id}`}>
                    <div className="flex justify-between text-sm">
                      <span className="text-white">
                        {s.taken_by_name}
                        <span className="text-[10px] text-slate-500 ml-2 uppercase">
                          {s.taken_by_role}
                        </span>
                      </span>
                      <span className="font-mono text-emerald-400">{money(s.total)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      {Object.entries(s.days).sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([day, amount]) => (
                          <span key={day} className="text-[11px] text-slate-400">
                            {day} <span className="font-mono text-slate-300">{money(amount)}</span>
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 uppercase text-[10px]">
                    <th className="text-left py-1 pr-3">Date</th>
                    <th className="text-left py-1 pr-3">Taken by</th>
                    <th className="text-left py-1 pr-3">Customer</th>
                    <th className="text-left py-1 pr-3">Scope</th>
                    <th className="text-right py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p, i) => (
                    <tr key={p.cash_payment_id} className="border-t border-slate-800"
                        data-testid={`cash-payment-row-${i}`}>
                      <td className="py-1 pr-3 text-slate-400">{p.taken_on}</td>
                      <td className="py-1 pr-3 text-slate-300">{p.taken_by_name}</td>
                      <td className="py-1 pr-3 text-slate-300">{p.customer_name}</td>
                      <td className="py-1 pr-3 text-slate-400">{p.scope}</td>
                      <td className="py-1 text-right font-mono text-emerald-400">
                        {money(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const PermissionsLegend = () => (
  <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
    <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-slate-600" />
    Permissions are off for everyone by default and are checked on every
    request — switching one off takes effect immediately. They unlock only
    what they name: never costs, margins, labour, invoices, stock values or
    Finance.
  </p>
);
